import {useState, useRef, useEffect} from 'react';
import {GoogleMap} from '@react-google-maps/api';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {cn} from './ui/utils';
import {Button} from './ui/button';
import {Textarea} from './ui/textarea';
import {Label} from './ui/label';
import {SoftCard} from './ui/modern-card';
import {Camera, MapPin, Upload, Trash2, Loader2} from 'lucide-react';
import {CloseButton} from './ui/close-button';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ReportFormData) => void;
  userLocation?: {
    latitude: number;
    longitude: number;
    address: string;
  };
  clickedLocation?: {
    lat: number;
    lng: number;
  } | null;
}

interface ReportFormData {
  description: string;
  photo?: File;
  location: {
    latitude: number;
    longitude: number;
    address: string;
  };
}

const mapContainerStyle = {
  width: '100%',
  height: '200px',
};

export default function ReportModal({isOpen, onClose, onSubmit, userLocation, clickedLocation}: ReportModalProps) {
  const [description, setDescription] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [location, setLocation] = useState({
    latitude: userLocation?.latitude || 49.4144,
    longitude: userLocation?.longitude || 2.8322,
    address: userLocation?.address || 'Position actuelle'
  });
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);

  // Fonction pour créer/mettre à jour le marker
  const updateMarkerPosition = (map?: google.maps.Map) => {
    const currentMap = map || mapRef.current;
    if (!currentMap || !location.latitude || !location.longitude) return;

    // Supprimer l'ancien marker s'il existe
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Créer le nouveau marker avec le même style que la homepage
    const size = 52;
    markerRef.current = new google.maps.Marker({
      position: {lat: location.latitude, lng: location.longitude},
      map: currentMap,
      draggable: true,
      title: 'Position du signalement',
      icon: {
        url: 'data:image/svg+xml;utf-8,' + encodeURIComponent(`
          <svg width="${size}" height="${size}" viewBox="0 0 24 28" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="11" r="3" stroke="white" stroke-width="1.5" fill="#3b82f6"/>
            <path d="M21 10.8889C21 15.7981 15.375 22 12 22C8.625 22 3 15.7981 3 10.8889C3 5.97969 7.02944 2 12 2C16.9706 2 21 5.97969 21 10.8889Z" stroke="white" stroke-width="1.5" fill="#3b82f6"/>
            <text x="12" y="14" text-anchor="middle" font-size="10" fill="white">🐗</text>
          </svg>
        `),
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(size / 2, size - 6),
      }
    });

    markerRef.current.addListener('dragend', (e: any) => {
      handleMapClick(e);
    });
  };

  // Initialiser la localisation quand la modale s'ouvre
  useEffect(() => {
    if (!isOpen) return;


    if (clickedLocation) {
      // Cas 1: Clic sur la carte - utiliser les coordonnées et faire du reverse geocoding
      setIsLoadingLocation(true);

      const performReverseGeocoding = async () => {
        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${clickedLocation.lat},${clickedLocation.lng}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`
          );
          const data = await response.json();
          const address = data.results[0]?.formatted_address || `Position: ${clickedLocation.lat.toFixed(6)}, ${clickedLocation.lng.toFixed(6)}`;

          setLocation({
            latitude: clickedLocation.lat,
            longitude: clickedLocation.lng,
            address: address
          });
        } catch (error) {
          console.error('Erreur reverse geocoding:', error);
          setLocation({
            latitude: clickedLocation.lat,
            longitude: clickedLocation.lng,
            address: `Position sélectionnée: ${clickedLocation.lat.toFixed(6)}, ${clickedLocation.lng.toFixed(6)}`
          });
        }
        setIsLoadingLocation(false);
      };

      performReverseGeocoding();
    } else if (userLocation) {
      // Cas 2: Bouton + - utiliser userLocation directement
      setLocation({
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
        address: userLocation.address
      });
    }
  }, [isOpen, clickedLocation, userLocation]); // Dépendances nécessaires mais stables maintenant

  // Mettre à jour le marker quand la position change
  useEffect(() => {
    if (mapRef.current && location.latitude && location.longitude) {
      updateMarkerPosition();
    }
  }, [location.latitude, location.longitude]);

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner un fichier image');
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        alert('La photo ne doit pas dépasser 10MB');
        return;
      }

      setPhoto(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleMapClick = (e: any) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      // Reverse geocoding
      fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`
      )
        .then(response => response.json())
        .then(data => {
          const address = data.results[0]?.formatted_address || 'Position sélectionnée';
          setLocation({
            latitude: lat,
            longitude: lng,
            address: address
          });
        })
        .catch(() => {
          setLocation({
            latitude: lat,
            longitude: lng,
            address: 'Position sélectionnée'
          });
        });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!description.trim()) {
      alert('Veuillez décrire la situation');
      return;
    }

    setIsSubmitting(true);

    try {
      const formData: ReportFormData = {
        description: description.trim(),
        photo: photo || undefined,
        location
      };

      await onSubmit(formData);

      // Reset form
      setDescription('');
      setPhoto(null);
      setPhotoPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      onClose();
    } catch (error) {
      alert('Erreur lors de l\'envoi du signalement');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setDescription('');
    setPhoto(null);
    setPhotoPreview(null);
    setIsLoadingLocation(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50" />
        <DialogPrimitive.Content className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "fixed top-[50%] left-[50%] z-50 translate-x-[-50%] translate-y-[-50%] shadow-lg duration-200",
          "sm:max-w-2xl w-full max-w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto p-0 bg-white rounded-2xl"
        )}>
          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="p-6 pb-4 border-b border-gray-100/50">
              <div className="flex items-center justify-between">
                <DialogPrimitive.Title className="text-xl flex items-center gap-2">
                  <span>🐗</span>
                  {clickedLocation ? 'Signalement à cette position' : 'Nouveau signalement'}
                </DialogPrimitive.Title>
                <CloseButton onClick={handleCancel} />
              </div>
              <DialogPrimitive.Description className="text-center text-gray-500 text-sm mt-2">
                {clickedLocation
                  ? 'Créer un signalement à la position sélectionnée'
                  : 'Créer un signalement à votre position actuelle'
                }
              </DialogPrimitive.Description>
            </div>

            <div className="p-6 space-y-6 flex-1">
              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description de la situation *</Label>
                <Textarea
                  id="description"
                  placeholder="Décrivez la situation : nombre de sangliers, comportement, localisation précise, dangers potentiels..."
                  value={description}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setDescription(e.target.value);
                    }
                  }}
                  rows={4}
                  maxLength={500}
                  className="resize-none rounded-xl border-gray-200 focus:border-primary focus:ring-primary placeholder:text-gray-400 placeholder:opacity-100"
                  required
                />
                <p className="text-xs text-muted-foreground text-right">
                  {description.length}/500 caractères
                </p>
              </div>

              {/* Photo */}
              <div className="space-y-3">
                <Label>Photo (facultative)</Label>

                {!photoPreview ? (
                  <div className="space-y-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full rounded-xl border-dashed border-2 border-gray-200 hover:border-gray-300 h-24 flex flex-col gap-2 transition-colors"
                    >
                      <Camera className="w-6 h-6 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Ajouter une photo</span>
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <SoftCard className="p-3 rounded-xl">
                    <div className="space-y-3">
                      <div className="relative">
                        <img
                          src={photoPreview}
                          alt="Prévisualisation"
                          className="w-full h-48 object-cover rounded-lg"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={handleRemovePhoto}
                          className="absolute top-2 right-2 rounded-full w-8 h-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>{photo?.name}</span>
                        <span>{photo?.size ? `${Math.round(photo.size / 1024)} KB` : ''}</span>
                      </div>
                    </div>
                  </SoftCard>
                )}
              </div>

              {/* Position GPS avec carte */}
              <div className="space-y-2">
                <Label>Position GPS</Label>
                {isLoadingLocation ? (
                  <SoftCard className="p-4 rounded-xl bg-gray-50/50">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm text-muted-foreground">Détection de votre position...</span>
                    </div>
                  </SoftCard>
                ) : (
                  <>
                    <SoftCard className="p-4 rounded-xl bg-gray-50/50">
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                        <div className="space-y-1 flex-1">
                          <p className="text-sm">{location.address}</p>
                        </div>
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                      </div>
                    </SoftCard>

                    <div className="mt-3">
                      <p className="text-xs text-muted-foreground mb-2">
                        Cliquez sur la carte pour ajuster la position si nécessaire
                      </p>
                      <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                        <GoogleMap
                          mapContainerStyle={mapContainerStyle}
                          center={{lat: location.latitude, lng: location.longitude}}
                          zoom={15}
                          onClick={handleMapClick}
                          onLoad={(map) => {
                            mapRef.current = map;
                            updateMarkerPosition(map);
                          }}
                          options={{
                            streetViewControl: false,
                            mapTypeControl: false,
                            fullscreenControl: false,
                            draggableCursor: 'crosshair',
                            draggingCursor: 'crosshair',
                          }}
                        >
                        </GoogleMap>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="p-6 pt-4 bg-gray-50/50">
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  className="flex-1 rounded-xl"
                  disabled={isSubmitting}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="flex-1 rounded-xl bg-gray-900 hover:bg-gray-800 text-white border-0"
                  disabled={isSubmitting || !description.trim() || isLoadingLocation}
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Envoi...
                    </div>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Valider
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}